const API_BASE = process.env.REACT_APP_API_URL || "";

// ---------------------------------------------------------------------------
// Auth token management (in-memory only -- never in localStorage)
// ---------------------------------------------------------------------------

let _accessToken = null;
let _onAuthError = null; // callback set by AuthContext
let _refreshPromise = null; // single in-flight refresh so parallel 401s coalesce

export function setAccessToken(token) { _accessToken = token; }
export function getAccessToken() { return _accessToken; }
export function setOnAuthError(fn) { _onAuthError = fn; }

// Silent refresh using the httpOnly cookie. Returns true on success, false otherwise.
// Parallel callers share a single in-flight request.
function tryRefresh() {
  if (_refreshPromise) return _refreshPromise;
  const base = API_BASE || window.location.origin;
  _refreshPromise = fetch(new URL("/api/auth/refresh", base), {
    method: "POST",
    cache: "no-store",
    credentials: "include",
  })
    .then(async (res) => {
      if (!res.ok) return false;
      const data = await res.json();
      _accessToken = data.access_token;
      return true;
    })
    .catch(() => false)
    .finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

async function apiFetch(path, params = {}, { method = "GET", body, auth = false, _retry = false } = {}) {
  const base = API_BASE || window.location.origin;
  const url = new URL(path, base);
  if (method === "GET") {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") {
        url.searchParams.set(k, v);
      }
    });
  }

  const headers = {};
  if (_accessToken && auth) {
    headers["Authorization"] = `Bearer ${_accessToken}`;
  }
  if (body) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    cache: "no-store",
    credentials: "include",
  });

  if (res.status === 401 && auth) {
    if (!_retry) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return apiFetch(path, params, { method, body, auth, _retry: true });
      }
    }
    if (_onAuthError) _onAuthError();
    throw new Error("Session expired");
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    // `detail` may be a structured object (e.g. the tracker limit_reached
    // payload); keep the raw value on the error and use a string for message.
    const detail = data.detail;
    const msg = typeof detail === "string" ? detail : `API error ${res.status}: ${res.statusText}`;
    const err = new Error(msg);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Auth endpoints
// ---------------------------------------------------------------------------

export const apiRegister = (email, password, display_name) =>
  apiFetch("/api/auth/register", {}, { method: "POST", body: { email, password, display_name } });

export const apiLogin = (email, password) =>
  apiFetch("/api/auth/login", {}, { method: "POST", body: { email, password } });

export const apiRefresh = () =>
  apiFetch("/api/auth/refresh", {}, { method: "POST" });

export const apiLogout = () =>
  apiFetch("/api/auth/logout", {}, { method: "POST" });

export const apiGetMe = () =>
  apiFetch("/api/auth/me", {}, { auth: true });

export const apiVerifyEmail = (token) =>
  apiFetch(`/api/auth/verify`, { token });

export const apiResendVerification = (email, password) =>
  apiFetch("/api/auth/resend-verification", {}, { method: "POST", body: { email, password } });

export const apiRequestPasswordReset = (email) =>
  apiFetch("/api/auth/request-password-reset", {}, { method: "POST", body: { email } });

export const apiResetPassword = (token, password) =>
  apiFetch("/api/auth/reset-password", {}, { method: "POST", body: { token, password } });

// ---------------------------------------------------------------------------
// Public endpoints (no auth required)
// ---------------------------------------------------------------------------

// Going Bias Explorer
export const getCourses = () => apiFetch("/api/courses");
export const getGoingStats = (id, filters) => apiFetch(`/api/courses/${id}/going-stats`, filters);
export const getDrawBias = (id, filters) => apiFetch(`/api/courses/${id}/draw-bias`, filters);
export const getFieldSizeStats = (id, filters) => apiFetch(`/api/courses/${id}/field-size-stats`, filters);
export const getTrends = (id, filters) => apiFetch(`/api/courses/${id}/trends`, filters);
export const getFavouriteStats = (id, filters) => apiFetch(`/api/courses/${id}/favourite-stats`, filters);
export const getCourseRaceTypes = (id) => apiFetch(`/api/courses/${id}/race-types`);
export const getCourseGoings = (id, filters) => apiFetch(`/api/courses/${id}/goings`, filters);
export const getCourseDistances = (id, filters) => apiFetch(`/api/courses/${id}/distances`, filters);

// Race-Level Going Bias Explorer
export const getRacesToday = () => apiFetch("/api/races/today");
export const getRaceGoingBias = (raceId) => apiFetch(`/api/races/${raceId}/going-bias`);
export const getRaceSurfaceBias = (raceId) => apiFetch(`/api/races/${raceId}/surface-bias`);
export const getRaceGoingBiasDifferential = (raceId) => apiFetch(`/api/races/${raceId}/going-bias-differential`);

// Trainer Profile Heatmap
export const getRaceTrainerHeatmap = (raceId) => apiFetch(`/api/races/${raceId}/trainer-heatmap`);

// Race Radar
export const getRaceRadar = (raceId) => apiFetch(`/api/races/${raceId}/race-radar`);

// Pace Map
export const getPaceMap = (raceId) => apiFetch(`/api/races/${raceId}/pace-map`);

// Jockey Profile Heatmap
export const getRaceJockeyHeatmap = (raceId) => apiFetch(`/api/races/${raceId}/jockey-heatmap`);

// Draw Bias Heatmap
export const getRaceDrawBias = (raceId) => apiFetch(`/api/races/${raceId}/draw-bias-heatmap`);

// Today's Card (single pre-computed endpoint)
export const getRaceCard = (raceId) => apiFetch(`/api/races/${raceId}/card`);

// Betfair Exchange prices (live)
export const getBetfairPrices = (raceId) => apiFetch(`/api/races/${raceId}/betfair-prices`);

// Intraday Betfair price history (sparkline) — { history: { horse_id: [{t, price}] } }
export const getPriceSnapshots = (raceId) => apiFetch(`/api/snapshots/${raceId}`);

// ---------------------------------------------------------------------------
// Protected endpoints (auth required)
// ---------------------------------------------------------------------------

// Tissue Pricer
export const getTissue = (raceId) => apiFetch(`/api/races/${raceId}/tissue`, {}, { auth: true });

// Tissue Backtest (legacy — kept for any in-flight callers; new code should use TissueRecent)
export const getBacktestRaces = () => apiFetch("/api/tissue-backtest/races");
export const getBacktestRace = (raceId) => apiFetch(`/api/tissue-backtest/${raceId}`);
export const getBacktestSummary = () => apiFetch("/api/tissue-backtest/summary");

// Member Tissue — last-N-meetings-per-(course, surface) viewer (uses members' v2 engines)
// The explorer fetches the course LIST once, then one course at a time. At the
// 10-meeting window the unscoped race list is ~1.6 MB of JSON for a sidebar
// that only ever shows one course; getTissueRecentRaces is kept because the
// frontend deployed before page 04 still calls it.
export const getTissueRecentRaces = () => apiFetch("/api/member/tissue/recent/races", {}, { auth: true });
export const getTissueRecentCourses = () => apiFetch("/api/member/tissue/recent/courses", {}, { auth: true });
export const getTissueRecentCourse = (courseId, window) =>
  apiFetch(
    `/api/member/tissue/recent/course/${courseId}${window ? `?window=${window}` : ""}`,
    {}, { auth: true },
  );
export const getTissueRecentRace = (raceId) => apiFetch(`/api/member/tissue/recent/${raceId}`, {}, { auth: true });
export const getTissueRecentMeta = () => apiFetch("/api/member/tissue/recent/meta", {}, { auth: true });

// Going Bias Backtest (AW + NH)
export const getGoingBiasBacktestRaces = (surface) => apiFetch(`/api/going-bias-backtest/races${surface ? `?surface=${surface}` : ""}`);
// Without `surface` this returns the legacy all-surface block; the redesigned
// page always passes one, plus the view parameters the builder precomputed.
export const getGoingBiasBacktestSummary = ({ surface, window, going, confidence, minBets } = {}) => {
  const qs = new URLSearchParams();
  if (surface) qs.set("surface", surface);
  if (window) qs.set("window", window);
  if (going && going !== "all") qs.set("going", going);
  if (confidence && confidence !== "all") qs.set("confidence", confidence);
  if (minBets) qs.set("min_bets", minBets);
  const q = qs.toString();
  return apiFetch(`/api/going-bias-backtest/summary${q ? `?${q}` : ""}`);
};
export const getGoingBiasBacktestRace = (raceId) => apiFetch(`/api/going-bias-backtest/${raceId}`);

// Draw Bias Backtest (AW)
export const getDrawBiasBacktestRaces = () => apiFetch("/api/draw-bias-backtest/races");
export const getDrawBiasBacktestSummary = (window, minBets) => {
  const qs = new URLSearchParams();
  if (window) qs.set("window", window);
  if (minBets) qs.set("min_bets", minBets);
  const q = qs.toString();
  return apiFetch(`/api/draw-bias-backtest/summary${q ? `?${q}` : ""}`);
};
export const getDrawBiasBacktestRace = (raceId) => apiFetch(`/api/draw-bias-backtest/${raceId}`);

// AW Picks
export const getAWPicksToday = () => apiFetch("/api/aw-picks/today");
export const getAWPicksYesterday = () => apiFetch("/api/aw-picks/yesterday");

// Cheltenham 2026
export const getCheltenhamRaces = () => apiFetch("/api/cheltenham/races");
export const getCheltenhamTissue = (raceKey) => apiFetch(`/api/cheltenham/${raceKey}/tissue`);
export const getCheltenhamCard = (raceKey) => apiFetch(`/api/cheltenham/${raceKey}/card`);
export const getCheltenhamRaceRadar = (raceKey) => apiFetch(`/api/cheltenham/${raceKey}/race-radar`);
export const getCheltenhamTrainerHeatmap = (raceKey) => apiFetch(`/api/cheltenham/${raceKey}/trainer-heatmap`);
export const getCheltenhamJockeyHeatmap = (raceKey) => apiFetch(`/api/cheltenham/${raceKey}/jockey-heatmap`);
export const getCheltenhamGoingBias = (raceKey) => apiFetch(`/api/cheltenham/${raceKey}/going-bias`);

// Mispriced (UK turf flat ratings framework — gated to subscription_tier
// in {beta, paid, lifetime}; backend returns 401 if no token, 403 if tier
// is 'free'. The client-side MemberRoute does the same tier check before
// the call is even made, so the API call only fires for known members.)
export const getMemberRacesToday = () => apiFetch("/api/member/races/today", {}, { auth: true });
export const getMispricedTissue = (raceId) => apiFetch(`/api/member/races/${raceId}/tissue`, {}, { auth: true });
export const getMemberGoingBiasToday = () => apiFetch("/api/member/going-bias/today", {}, { auth: true });
export const getMemberGoingBiasNhToday = () => apiFetch("/api/member/going-bias/nh/today", {}, { auth: true });
export const getMemberRaceTrainerStats = (raceId) => apiFetch(`/api/member/races/${raceId}/trainer-stats`, {}, { auth: true });
export const getMemberRaceTrainerStatsNh = (raceId) => apiFetch(`/api/member/races/${raceId}/trainer-stats-nh`, {}, { auth: true });
export const getMemberRaceBreeding = (raceId) => apiFetch(`/api/member/races/${raceId}/breeding`, {}, { auth: true });
export const getMemberRaceBreedingNh = (raceId) => apiFetch(`/api/member/races/${raceId}/breeding-nh`, {}, { auth: true });
export const getFormGraphs = (raceId) => apiFetch(`/api/member/form-graphs/${raceId}`, {}, { auth: true });
export const getMemberRaceBreedingAw = (raceId) => apiFetch(`/api/member/races/${raceId}/breeding-aw`, {}, { auth: true });
export const getMemberRaceTrainerStatsAw = (raceId) => apiFetch(`/api/member/races/${raceId}/trainer-stats-aw`, {}, { auth: true });
export const getMemberCourseBiasAw = () => apiFetch("/api/member/course-bias/aw", {}, { auth: true });
export const getMemberTodayStrongest = () => apiFetch("/api/member/today-strongest", {}, { auth: true });
export const getMemberTodayStrongestBacktest = () => apiFetch("/api/member/today-strongest/backtest", {}, { auth: true });
export const getMemberTissueBacktest = () => apiFetch("/api/member/tissue/backtest", {}, { auth: true });
export const getMemberTissueNhBacktest = () => apiFetch("/api/member/tissue/nh/backtest", {}, { auth: true });
export const getMemberTissueAwBacktest = () => apiFetch("/api/member/tissue/aw/backtest", {}, { auth: true });
export const getMemberNhRacesToday = () => apiFetch("/api/member/races/nh/today", {}, { auth: true });
export const getMispricedTissueNh = (raceId) => apiFetch(`/api/member/races/${raceId}/tissue-nh`, {}, { auth: true });
export const getMemberAwRacesToday = () => apiFetch("/api/member/races/aw/today", {}, { auth: true });
export const getMispricedTissueAw = (raceId) => apiFetch(`/api/member/races/${raceId}/tissue-aw`, {}, { auth: true });

// Public "big race" showcases (config-driven; see src/config/showcases.json).
export const getShowcaseTissue = (slug) => apiFetch(`/api/showcase/${slug}/tissue`);
export const getShowcaseFormGraphs = (slug) => apiFetch(`/api/showcase/${slug}/form-graphs`);
export const getShowcaseSnapshots = (slug) => apiFetch(`/api/showcase/${slug}/snapshots`);
export const getMemberPicks = () => apiFetch("/api/member/model-picks", {}, { auth: true });
// Track record is member-gated (2026-05-13) — backend requires
// require_member, frontend route is wrapped in <MemberRoute>.
export const getTrackRecord = () => apiFetch("/api/track-record", {}, { auth: true });

// Lincoln / Doncaster Saturday
export const getLincolnRaces = () => apiFetch("/api/lincoln/races");
export const getLincolnTissue = (raceKey) => apiFetch(`/api/lincoln/${raceKey}/tissue`);
export const getLincolnCard = (raceKey) => apiFetch(`/api/lincoln/${raceKey}/card`);
export const getLincolnRaceRadar = (raceKey) => apiFetch(`/api/lincoln/${raceKey}/race-radar`);
export const getLincolnTrainerHeatmap = (raceKey) => apiFetch(`/api/lincoln/${raceKey}/trainer-heatmap`);
export const getLincolnJockeyHeatmap = (raceKey) => apiFetch(`/api/lincoln/${raceKey}/jockey-heatmap`);
export const getLincolnGoingBias = (raceKey) => apiFetch(`/api/lincoln/${raceKey}/going-bias`);

// Natural-language query interface (member-gated). See nl_query/README.md.
// Two-step flow: preview (paraphrase + SQL + columns) -> user confirms ->
// run (execute). The legacy apiNLQuery one-shot is kept for any in-flight
// callers but new UI uses preview + run.
export const apiNLQuery = (question) =>
  apiFetch("/api/nl-query", {}, { method: "POST", body: { question }, auth: true });

// `raceContext` (optional) is the lean payload from a Race Previews deep-link
// ("Ask about this race"). When present the backend bypasses the shared cache.
export const apiNLQueryPreview = (question, columns = null, raceContext = null) =>
  apiFetch("/api/nl-query/preview", {}, {
    method: "POST",
    body: { question, columns, ...(raceContext ? { race_context: raceContext } : {}) },
    auth: true,
  });

export const apiNLQueryRun = (question, sql, raceContext = null) =>
  apiFetch("/api/nl-query/run", {}, {
    method: "POST",
    body: { question, sql, ...(raceContext ? { race_context: raceContext } : {}) },
    auth: true,
  });

// Per-user "asked earlier today" hint list, populated by the backend's
// today-scoped query cache. Clicking one resubmits and hits the cache
// (no token cost).
export const apiNLQueryRecent = () =>
  apiFetch("/api/nl-query/recent", {}, { auth: true });

// ---------------------------------------------------------------------------
// Race Previews -- FREE, public (no auth). Reads the nightly-cached previews.
// ---------------------------------------------------------------------------
export const apiPreviewsToday = () =>
  apiFetch("/api/previews/today", {}, {});

export const apiPreviewsUpcoming = () =>
  apiFetch("/api/previews/upcoming", {}, {});

export const apiPreviewsByDate = (date) =>
  apiFetch(`/api/previews/by-date/${date}`, {}, {});

export const apiPreview = (raceId) =>
  apiFetch(`/api/previews/${raceId}`, {}, {});

// ---------------------------------------------------------------------------
// Tracker -- horse / trainer / jockey follow lists (logged-in users)
// ---------------------------------------------------------------------------

export const trackerSearch = (q, types = "horse,trainer,jockey", limit = 8) =>
  apiFetch("/api/tracker/search", { q, types, limit }, { auth: true });

export const trackerList = () =>
  apiFetch("/api/tracker/list", {}, { auth: true });

export const trackerAdd = ({ entity_type, entity_id, entity_name, note, alert_mode }) =>
  apiFetch("/api/tracker/track", {}, {
    method: "POST", auth: true,
    body: { entity_type, entity_id, entity_name, note, alert_mode },
  });

export const trackerUpdate = (entryId, patch) =>
  apiFetch(`/api/tracker/entries/${entryId}`, {}, { method: "PATCH", auth: true, body: patch });

export const trackerDelete = (entryId) =>
  apiFetch(`/api/tracker/entries/${entryId}`, {}, { method: "DELETE", auth: true });

export const trackerUpcoming = (days = 7) =>
  apiFetch("/api/tracker/upcoming", { days }, { auth: true });

export const trackerSetDigest = (enabled) =>
  apiFetch("/api/tracker/digest", {}, { method: "POST", auth: true, body: { enabled } });

// Public (token-based) one-click unsubscribe from the email link.
export const trackerUnsubscribe = (token) =>
  apiFetch("/api/tracker/unsubscribe", { token }, {});
